/**
 * USGS Water Data for the Nation — OGC API client.
 *
 * Domain responsibilities:
 *   - Southern Maine station and bounding-box conventions
 *   - Parameter code → human-readable name mapping
 *   - Sentinel value (-999999) filtering
 *   - Result formatting for LLM context injection
 *
 * Migrated from legacy waterservices.usgs.gov/nwis/iv/ (decommissioning early 2027,
 * intentional degradation no earlier than August 2026) to the new OGC Water Data API:
 * https://api.waterdata.usgs.gov/ogcapi/v0/
 *
 * Migration reference:
 *   https://waterdata.usgs.gov/blog/user_migration_blog/
 *
 * Environment:
 *   (none required — public API, no key)
 */

const USGS_BASE = "https://api.waterdata.usgs.gov/ogcapi/v0";

/** Southern Maine monitoring stations of interest */
const SOUTHERN_MAINE_STATIONS = [
  "USGS-01064140", // Presumpscot River near West Falmouth, ME (primary)
  "USGS-01060000", // Saco River at Cornish, ME
  "USGS-01066000", // Little Androscoggin River near South Paris, ME
];

/** USGS parameter codes → fishing-relevant display names */
const PARAM_NAMES: Record<string, string> = {
  "00010": "Water Temp (°C)",
  "00060": "Discharge (ft³/s)",
  "00065": "Gage Height (ft)",
  "00300": "Dissolved Oxygen (mg/L)",
};

interface UsgsObservation {
  id: string;
  properties?: {
    "monitoring-location-id"?: string;
    "monitoring-location-name"?: string;
    parameterCode?: string;
    result?: string;
    phenomenonTime?: string;
  };
}

/**
 * Fetch latest water conditions for a specific station or all Southern Maine stations.
 *
 * @param stationId - USGS monitoring location ID (e.g. "USGS-01064140").
 *                    Omit to query all Southern Maine stations.
 * @returns Human-readable conditions string suitable for LLM context injection.
 */
export async function getWaterConditions(stationId?: string): Promise<string> {
  const stationsToQuery = stationId ? [stationId] : SOUTHERN_MAINE_STATIONS;

  const results: string[] = [];

  await Promise.allSettled(
    stationsToQuery.map(async (id) => {
      try {
        const url = new URL(
          `${USGS_BASE}/collections/monitoring-locations/items/${encodeURIComponent(id)}/observations`
        );
        url.searchParams.set("f", "json");
        url.searchParams.set("resultType", "latest");

        const res = await fetch(url.toString(), {
          headers: {
            Accept: "application/geo+json",
            "User-Agent": "FlyFishME/1.0 (field guide PWA)",
          },
        });

        if (!res.ok) return;

        const data = await res.json();
        const features: UsgsObservation[] = data?.features ?? [];

        for (const f of features) {
          const props = f.properties;
          if (!props) continue;

          const name = props["monitoring-location-name"] ?? id;
          const code = props.parameterCode ?? "";
          const value = props.result ?? "";
          const time = props.phenomenonTime ?? "";

          // Filter sentinel "no data" values
          if (value === "-999999" || value === "") continue;

          const label = PARAM_NAMES[code] ?? code;
          results.push(`${name} (${id}): ${label} = ${value} at ${time}`);
        }
      } catch {
        // Individual station failure is non-fatal; degrade gracefully
      }
    })
  );

  return results.length > 0
    ? results.join("\n")
    : "No current USGS data available for Southern Maine stations.";
}

/**
 * Fetch GeoJSON of all active USGS monitoring locations in Southern Maine.
 * Used for map rendering (stream gauge markers).
 *
 * @returns GeoJSON FeatureCollection or null on failure.
 */
export async function getMonitoringLocationsGeoJSON(): Promise<unknown | null> {
  try {
    const url = new URL(`${USGS_BASE}/collections/monitoring-locations/items`);
    url.searchParams.set("f", "json");
    url.searchParams.set("stateCd", "ME");
    url.searchParams.set("limit", "200");

    const res = await fetch(url.toString(), {
      headers: {
        Accept: "application/geo+json",
        "User-Agent": "FlyFishME/1.0 (field guide PWA)",
      },
    });

    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * Structured water reading — for UI components that need individual values
 * rather than the LLM-formatted string from getWaterConditions().
 */
export interface RiverReading {
  stationId: string;
  stationName: string;
  paramLabel: string;
  value: string;
  time: string;
}

/**
 * Fetch the latest readings for a single station, returned as structured data
 * suitable for rendering in a UI card.
 *
 * @param stationId — e.g. "USGS-01064140" (Presumpscot at West Falmouth)
 */
export async function getStationReadings(stationId: string): Promise<RiverReading[]> {
  try {
    const url = new URL(
      `${USGS_BASE}/collections/monitoring-locations/items/${encodeURIComponent(stationId)}/observations`
    );
    url.searchParams.set("f", "json");
    url.searchParams.set("resultType", "latest");

    const res = await fetch(url.toString(), {
      headers: {
        Accept: "application/geo+json",
        "User-Agent": "FlyFishME/1.0 (field guide PWA)",
      },
    });

    if (!res.ok) return [];

    const data = await res.json();
    const features: UsgsObservation[] = data?.features ?? [];
    const readings: RiverReading[] = [];

    for (const f of features) {
      const props = f.properties;
      if (!props) continue;
      const value = props.result ?? "";
      if (value === "-999999" || value === "") continue;

      readings.push({
        stationId,
        stationName: props["monitoring-location-name"] ?? stationId,
        paramLabel: PARAM_NAMES[props.parameterCode ?? ""] ?? (props.parameterCode ?? ""),
        value,
        time: props.phenomenonTime ?? "",
      });
    }

    return readings;
  } catch {
    return [];
  }
}
